import { UiRenderer } from "@agent/UiRenderer";
import { isUiPayload, type UiPayload } from "@agent/uiSchema";
import {
  faClock,
  faFileLines,
  faSignature,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  cn,
  FilingLine,
  IndexCard,
  Stamp,
} from "@scribenote/brian-design-system";
import {
  type CardSeverity,
  SeverityBadge,
  severityReaderChrome,
} from "@subjects/cardSeverity";
import { HUMAN, SIGN, type Task, type TaskViewRef } from "@tasks/tasksClient";
import { useTaskAssignee } from "@tasks/useTaskAssignee";
import { useTaskSigning } from "@tasks/useTaskSigning";
import { formatDateTime, formatPastDateTime } from "@utils/format-date";
import { useWorkspaceNavigation } from "@workspace/useWorkspaceNavigation";
import { type ReactNode, useMemo } from "react";

/** What every task action does, threaded down from `useTaskActions`. */
type TaskActionHandler = (
  task: Task,
  action: string,
  data: Record<string, unknown>,
) => void;

/**
 * One open task as a physical index card (WEB-3328), the /tasks route's
 * presentation: a thing to do, written down, handed over, thrown out when done.
 * The title is written on the first rule, the agent's reason and the filing line
 * below it, and whatever resolves the card lives on the strip under the rules,
 * because the ruled face says only what a person wrote on it.
 *
 * The three shapes a task comes in are the three the backend defines, and they
 * resolve through different endpoints: `sign` acts on a view, an a2ui payload
 * answers itself through `UiRenderer`, and a plain `human` to-do takes Mark done
 * / Dismiss. `visit_workflow` is internal and offers nothing.
 *
 * Deliberately separate from `TaskCard`/`TaskCardItem`, the plain paper sheet
 * every other surface renders a task on (the subject pane, the notifications
 * feed). The shared contract lives in the hooks both call, not in
 * a variant flag: the two presentations have nothing in common but the data.
 */
export const TaskIndexCard = ({
  task,
  severity,
  handleAction,
  stamped = false,
  onSigning,
  onSignSettled,
}: {
  task: Task;
  severity: CardSeverity;
  handleAction: TaskActionHandler;
  /** The signature landed and the list is holding this card for its stamp
      (`useSignCeremony`). Only a `sign` task has one to show. */
  stamped?: boolean;
  /** Fired as the Sign button dispatches, so the list can hold the card long
      enough to stamp it before the refetch drops it. */
  onSigning?: () => void;
  /** Fired with what the server ruled, so the list stamps a signature that
      actually landed rather than one that merely left the list. */
  onSignSettled?: (signed: boolean) => void;
}) => {
  if (task.kind === SIGN)
    return (
      <SignTaskIndexCard
        task={task}
        severity={severity}
        view={task.views[0]}
        stamped={stamped}
        onSigning={onSigning}
        onSignSettled={onSignSettled}
        onDecline={() => handleAction(task, "dismiss", {})}
      />
    );

  // Narrowed here rather than inside the card: the guard proves the payload is a
  // UI tree, and passing `task` alone would lose that.
  if (isUiPayload(task.payload))
    return (
      <A2uiTaskIndexCard
        task={task}
        severity={severity}
        payload={task.payload}
        handleAction={handleAction}
      />
    );

  return (
    <TaskIndexCardFace
      task={task}
      severity={severity}
      dataTestId={`task-card-${task.id}`}
      // Only a plain `human` to-do has resolutions a person can pick: a
      // `visit_workflow` task is the agent's own bookkeeping (WEB-3199).
      band={
        task.kind === HUMAN ? (
          <ActionRow>
            <Button
              variant="outline"
              size="sm"
              data-testid={`task-complete-${task.id}`}
              onClick={() => handleAction(task, "complete", {})}
            >
              Mark done
            </Button>
            <Button
              variant="ghost"
              size="sm"
              data-testid={`task-dismiss-${task.id}`}
              onClick={() => handleAction(task, "dismiss", {})}
            >
              Dismiss
            </Button>
          </ActionRow>
        ) : undefined
      }
    />
  );
};

/**
 * A `needs_clarification` / `pims_write` task: the agent's own form, rendered by
 * `UiRenderer` (WEB-3199). The form sits in the card's bottom strip rather than
 * on the ruling, because it brings its own card surface with it. A card written
 * over the rules would fight them, and the strip is where a card's controls
 * already live. The title, severity and filing line stay written on the face, so
 * an a2ui task reads as the same object as every other card in the list.
 *
 * No dismiss: the full list is not the place to bury an open question (BE-1877).
 */
const A2uiTaskIndexCard = ({
  task,
  severity,
  payload,
  handleAction,
}: {
  task: Task;
  severity: CardSeverity;
  payload: UiPayload;
  handleAction: TaskActionHandler;
}) => {
  // Memoized because `UiRenderer` seeds its input store off the payload's
  // identity: a fresh object per render would re-run that effect every render.
  const written = useMemo(
    () => withoutEchoedTitle(payload, task.title),
    [payload, task.title],
  );

  return (
    <TaskIndexCardFace
      task={task}
      severity={severity}
      dataTestId={`task-card-${task.id}`}
      band={
        <div className="p-1.5">
          <UiRenderer
            payload={written}
            storeKey={task.id}
            onAction={(action, data) => handleAction(task, action, data)}
            allowDismiss={false}
          />
        </div>
      }
    />
  );
};

const collapsed = (text: string) =>
  text.trim().replace(/\s+/g, " ").toLowerCase();

/**
 * The payload with its leading heading dropped when that heading only repeats
 * the task title, so an a2ui card doesn't say its name twice.
 *
 * Backend-authored cards write the two from the same string: brian's
 * `modules/pims_xref/clarification.py` headings are verbatim the titles its
 * `_raise_clarification(title=...)` calls pass, and an agent composing a card
 * through `render_ui` is free to do the same. The index card writes the title on
 * its first rule, so the copy inside the band is the one that goes.
 *
 * Only a leading `title`/`heading` `text` node, and only when the words match: a
 * heading that says something new (a `pims_write` card's "Enter in the PIMS -
 * Bella" under its own instruction title) is the card's own and stays.
 */
export const withoutEchoedTitle = (
  payload: UiPayload,
  title: string,
): UiPayload => {
  const root = payload.root;
  if (!("children" in root)) return payload;
  const [first, ...rest] = root.children ?? [];
  if (first?.type !== "text") return payload;
  if (first.variant !== "title" && first.variant !== "heading") return payload;
  if (collapsed(first.text) !== collapsed(title)) return payload;
  return { ...payload, root: { ...root, children: rest } };
};

/**
 * A `sign` task (BE-2034): the card names the view it points at and carries the
 * signature itself, since signing acts on the view rather than the task.
 *
 * Nothing here is optimistic: the Sign button goes busy and the card only says
 * "Signed" once the server has said so (see `useTaskSigning`). The stamp is a
 * hint at that point, not the record: the signature belongs to the view, and the
 * view says who signed it and when.
 *
 * Signing from /tasks closes the task, so the card is on its way out. `stamped`
 * is the list holding it back for a beat (`useSignCeremony`): the stamp thunks
 * down, the actions go, and then the card leaves.
 */
const SignTaskIndexCard = ({
  task,
  severity,
  view,
  stamped,
  onSigning,
  onSignSettled,
  onDecline,
}: {
  task: Task;
  severity: CardSeverity;
  /** The view being signed, i.e. `task.views[0]`. */
  view: TaskViewRef | undefined;
  stamped: boolean;
  onSigning?: () => void;
  onSignSettled?: (signed: boolean) => void;
  onDecline: () => void;
}) => {
  const { setViewedView } = useWorkspaceNavigation();
  const { isSigner, alreadySigned, canAct, isPending, sign } = useTaskSigning(
    view,
    onSignSettled,
  );

  return (
    <TaskIndexCardFace
      task={task}
      severity={severity}
      dataTestId={`sign-task-${task.id}`}
      stamp={
        // Animated only for a signature that just landed: an already-signed card
        // was signed before this list was drawn, and nothing was applied here.
        stamped || alreadySigned ? (
          <Stamp
            tone="accent"
            animateIn={stamped}
            data-testid={`sign-task-stamp-${task.id}`}
          >
            Signed
          </Stamp>
        ) : undefined
      }
      band={
        view ? (
          <ActionRow>
            <Button
              variant="ghost"
              size="sm"
              data-testid={`sign-task-open-view-${task.id}`}
              onClick={() => setViewedView(view.id)}
            >
              <FontAwesomeIcon icon={faFileLines} aria-hidden="true" />
              Open view
            </Button>
            {canAct && !stamped && (
              <>
                {/* The button hints the stamp it applies: mono, uppercase, in
                    the accent the pad is inked with. The colors drop while the
                    signature is in flight so the inert button reads inert. */}
                <Button
                  variant="outline"
                  size="sm"
                  busy={isPending}
                  data-testid={`view-sign-${task.id}`}
                  onClick={() => {
                    onSigning?.();
                    sign();
                  }}
                  className={cn(
                    "font-mono uppercase tracking-[0.13em]",
                    !isPending && "border-accent-deep text-accent-deep",
                  )}
                >
                  <FontAwesomeIcon icon={faSignature} aria-hidden="true" />
                  Sign
                </Button>
                {/* Shut while a signature is in flight: the two resolve the
                    same card opposite ways, and the server has already been
                    asked to sign it. */}
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isPending}
                  data-testid={`view-task-dismiss-${task.id}`}
                  onClick={onDecline}
                >
                  Decline
                </Button>
              </>
            )}
          </ActionRow>
        ) : undefined
      }
    >
      {view ? (
        <span
          className="block truncate text-[0.8125rem] text-annotation"
          data-testid={`sign-task-view-${task.id}`}
        >
          {view.name}
        </span>
      ) : (
        <span className="block text-[0.8125rem] text-annotation">
          Open the view to sign it.
        </span>
      )}
      {view && !alreadySigned && !isSigner && (
        <span className="block text-[0.8125rem] text-annotation">
          Awaiting sign-off by a veterinarian.
        </span>
      )}
    </TaskIndexCardFace>
  );
};

/**
 * The written face every task card shares. Line by line down the ruling: the
 * title, the agent's reason for flagging it, whatever the card adds
 * (`children`), then the filing line in mono: owner, due date, when it was
 * raised, where it stands.
 *
 * No card takes a pin. Pins belong on cork, and this stack lies on a page
 * (`bg-paper`): a pin here would be a pin through the desk. The corkboard's room
 * tiles are the pinned objects. Severity is the card's condition, not a line of
 * text: `watch` and `urgent` outline the card in their status ink and say the
 * word under a highlighter. A calm card is a clean card.
 */
const TaskIndexCardFace = ({
  task,
  severity,
  dataTestId,
  stamp,
  band,
  children,
}: {
  task: Task;
  severity: CardSeverity;
  dataTestId: string;
  /** A rubber stamp beside the title, for a mark a person left (sign-off). */
  stamp?: ReactNode;
  /** The strip under the rules: what resolves this card. */
  band?: ReactNode;
  /** Extra lines written between the reason and the filing line. */
  children?: ReactNode;
}) => {
  const chrome = severityReaderChrome(severity);
  const assignee = useTaskAssignee(task);

  return (
    <IndexCard
      data-testid={dataTestId}
      // A ring rather than a border: the card's hairline is its edge, and status
      // ink belongs outside it, on the card's outermost box so it tracks that
      // edge exactly. `chrome.ring` is spelled out in cardSeverity, so Tailwind
      // sees both colors literally. Two strokes, as on the room tiles: at one it
      // reads as a stray second hairline.
      className={cn(chrome && "ring-2", chrome?.ring)}
      header={
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1">{task.title}</span>
          {stamp && <OnTheRule>{stamp}</OnTheRule>}
          {chrome && (
            <OnTheRule>
              <SeverityBadge
                severity={severity}
                dataTestId={`task-severity-${task.id}`}
              />
            </OnTheRule>
          )}
        </span>
      }
      band={band}
    >
      {/* The reason is what the severity stands for, so it only appears with
          one, and a calm card has nothing to explain. */}
      {chrome && task.reason && (
        <span className="block text-[0.8125rem] text-annotation">
          {task.reason}
        </span>
      )}
      {children}
      <FilingLine>
        {assignee && (
          <span
            className="flex items-center gap-1.5"
            data-testid={`task-assignee-${task.id}`}
          >
            <FontAwesomeIcon icon={assignee.icon} aria-hidden="true" />
            {assignee.name}
          </span>
        )}
        {/* Compact on the rules, exact in the tooltip: two full locale stamps
            with seconds fill the line and settle nothing. `formatPastDateTime`
            is the compact form, and reads a due date the same way: today is a
            bare time, anything else carries its date. */}
        {task.dueAt && (
          <span
            className="flex items-center gap-1.5"
            title={formatDateTime(task.dueAt)}
          >
            <FontAwesomeIcon icon={faClock} aria-hidden="true" />
            Due {formatPastDateTime(task.dueAt)}
          </span>
        )}
        <span title={formatDateTime(task.createdAt)}>
          Raised {formatPastDateTime(task.createdAt)}
        </span>
        <span className="capitalize" data-testid={`task-status-${task.id}`}>
          {task.status.replace(/_/g, " ")}
        </span>
      </FilingLine>
    </IndexCard>
  );
};

/**
 * A chip written on a ruled line: a stamp, or a status word under a highlighter.
 *
 * Two things go wrong when one is dropped straight onto the ruling. A `Swipe`'s
 * padding is sized in em against a normal line-height, but the writing area's
 * line-height is the 24px ruling period, so the wash inflates into a slab that
 * pushes the line box past one rule and every line under it drifts off the
 * ruling. And a swipe is marker ink (`mix-blend-mode: multiply`), so it takes
 * whatever is behind it: on a ruled card that is the rule, striping the
 * highlighter.
 *
 * `leading-none` sizes the chip off its own type, so it fits inside one rule, and
 * the paper behind it knocks that rule out, so the marker lands on card stock
 * the way a highlighter does.
 */
const OnTheRule = ({ children }: { children: ReactNode }) => (
  <span className="inline-flex shrink-0 items-center rounded-swipe bg-card leading-none">
    {children}
  </span>
);

/** The card's bottom strip as an action row: the buttons sit in the band's own
 * recess, so they read as attached to the card rather than written on it. */
const ActionRow = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-wrap items-center gap-2 px-1">{children}</div>
);
